import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import GenerateOrdoreMiniLicenseModal from '@/components/admin/GenerateOrdoreMiniLicenseModal'
import RenewOrdoreMiniLicenseModal from '@/components/admin/RenewOrdoreMiniLicenseModal'
import OrdoreMiniLicenseDetailPanel from '@/components/admin/OrdoreMiniLicenseDetailPanel'
import DeleteOrdoreMiniLicenseModal from '@/components/admin/DeleteOrdoreMiniLicenseModal'
import {
  Receipt,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Copy,
  Eye,
  RefreshCw,
  XCircle,
  MessageCircle,
  AlertTriangle,
  CheckCircle2,
  Users,
  CalendarClock,
  Ban,
  Trash2
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ApiService } from '@/services/api'
import type { OrdoreMiniLicense, OrdoreMiniLicenseSummary } from '@/services/api'
import { toast } from 'sonner'

type StatusFilter = 'all' | 'active' | 'expiring_soon' | 'expired' | 'revoked'
type SortKey = 'restaurantName' | 'ownerName' | 'ownerPhone' | 'status' | 'issuedAt' | 'expiresAt' | 'daysLeft' | 'lastSyncedAt'

const PAGE_SIZE = 20

function statusBadge(status: OrdoreMiniLicense['status']) {
  switch (status) {
    case 'ACTIVE':
      return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>
    case 'GRACE':
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Grace</Badge>
    case 'EXPIRED':
      return <Badge className="bg-red-100 text-red-800 border-red-200">Expired</Badge>
    case 'REVOKED':
      return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Revoked</Badge>
  }
}

function daysLeftDisplay(daysLeft: number) {
  if (daysLeft <= 0) {
    return <span className="text-red-600 font-medium">Expired</span>
  }
  if (daysLeft <= 7) {
    return (
      <span className="flex items-center gap-1 text-red-600 font-medium">
        <AlertTriangle className="h-3.5 w-3.5" />
        {daysLeft}d
      </span>
    )
  }
  if (daysLeft <= 30) {
    return <span className="text-amber-600 font-medium">{daysLeft}d</span>
  }
  return <span className="text-green-600 font-medium">{daysLeft}d</span>
}

function lastSyncDisplay(lastSyncedAt: string | null) {
  if (!lastSyncedAt) {
    return <span className="text-red-600">Never</span>
  }
  const diffMs = Date.now() - new Date(lastSyncedAt).getTime()
  const hours = diffMs / (1000 * 60 * 60)
  const relative = formatDistanceToNow(new Date(lastSyncedAt), { addSuffix: true })

  if (hours > 7 * 24) {
    return <span className="text-red-600">Never</span>
  }
  if (hours > 24) {
    return <span className="text-amber-600">{relative}</span>
  }
  return <span className="text-foreground">{relative}</span>
}

export default function OrdoreMiniScreen() {
  const [licenses, setLicenses] = useState<OrdoreMiniLicense[]>([])
  const [summary, setSummary] = useState<OrdoreMiniLicenseSummary | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const [sortKey, setSortKey] = useState<SortKey>('expiresAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [renewingLicense, setRenewingLicense] = useState<OrdoreMiniLicense | null>(null)
  const [detailLicense, setDetailLicense] = useState<OrdoreMiniLicense | null>(null)
  const [deletingLicense, setDeletingLicense] = useState<OrdoreMiniLicense | null>(null)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadLicenses = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getOrdoreMiniLicenses({
        search: search || undefined,
        status: statusFilter,
        page,
        limit: PAGE_SIZE
      })

      if (response.success) {
        setLicenses(response.data.licenses)
        setTotal(response.data.total)
        setTotalPages(response.data.totalPages)
        setSummary(response.data.summary)
      } else {
        setError(response.message || 'Failed to load licenses')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load licenses')
    } finally {
      setIsLoading(false)
    }
  }, [search, statusFilter, page])

  useEffect(() => {
    loadLicenses()
  }, [loadLicenses])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sortedLicenses = useMemo(() => {
    const sorted = [...licenses].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'restaurantName':
          cmp = a.restaurantName.localeCompare(b.restaurantName)
          break
        case 'ownerName':
          cmp = a.ownerName.localeCompare(b.ownerName)
          break
        case 'ownerPhone':
          cmp = a.ownerPhone.localeCompare(b.ownerPhone)
          break
        case 'status':
          cmp = a.status.localeCompare(b.status)
          break
        case 'issuedAt':
          cmp = new Date(a.issuedAt).getTime() - new Date(b.issuedAt).getTime()
          break
        case 'expiresAt':
          cmp = new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime()
          break
        case 'daysLeft':
          cmp = a.daysLeft - b.daysLeft
          break
        case 'lastSyncedAt':
          cmp = new Date(a.lastSyncedAt || 0).getTime() - new Date(b.lastSyncedAt || 0).getTime()
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [licenses, sortKey, sortDir])

  const handleGenerateSuccess = () => {
    loadLicenses()
  }

  const handleRenewSuccess = (updated: OrdoreMiniLicense) => {
    setLicenses(prev => prev.map(l => (l._id === updated._id ? { ...l, ...updated, daysLeft: l.daysLeft } : l)))
    loadLicenses()
    if (detailLicense?._id === updated._id) setDetailLicense(updated)
  }

  const handleRevoke = async (license: OrdoreMiniLicense) => {
    if (!confirm(`Revoke the license for ${license.restaurantName}? This action cannot be undone.`)) {
      return
    }
    try {
      const response = await ApiService.revokeOrdoreMiniLicense(license._id)
      if (response.success) {
        toast.success('License revoked')
        setDetailLicense(null)
        loadLicenses()
      } else {
        toast.error(response.message || 'Failed to revoke license')
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to revoke license')
    }
  }

  const handleDeleteSuccess = (id: string) => {
    setLicenses(prev => prev.filter(l => l._id !== id))
    if (detailLicense?._id === id) setDetailLicense(null)
    loadLicenses()
  }

  const handleCopyKey = (license: OrdoreMiniLicense) => {
    navigator.clipboard.writeText(license.licenseKey)
    toast.success('License key copied to clipboard')
  }

  const handleWhatsApp = (license: OrdoreMiniLicense) => {
    const digits = license.ownerPhone.replace(/\D/g, '')
    const text = encodeURIComponent(
      `Hi, your Ordore Mini license key is: ${license.licenseKey}. Valid for ${license.validityDays} days. Enter this key in the Ordore Mini app to activate your account.`
    )
    window.open(`https://wa.me/${digits}?text=${text}`, '_blank')
  }

  const SortHeader = ({ sortKeyName, label }: { sortKeyName: SortKey; label: string }) => (
    <button
      type="button"
      className="flex items-center gap-1 hover:text-foreground"
      onClick={() => handleSort(sortKeyName)}
    >
      {label}
      {sortKey === sortKeyName && (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
    </button>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6" />
            Ordore Mini
          </h1>
          <p className="text-muted-foreground">
            Manage Ordore Mini POS licenses for restaurants
          </p>
        </div>
        <Button onClick={() => setShowGenerateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Generate New License
        </Button>
      </div>

      {/* Sticky alert banner */}
      {summary && summary.expiringWithin7Days > 0 && (
        <Alert className="border-amber-200 bg-amber-50 sticky top-0 z-10">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between text-amber-800">
            <span>
              {summary.expiringWithin7Days} restaurant{summary.expiringWithin7Days !== 1 ? 's' : ''} expiring within 7 days
            </span>
            <Button
              variant="link"
              className="text-amber-800 underline p-0 h-auto"
              onClick={() => setStatusFilter('expiring_soon')}
            >
              view them
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-xl font-bold">{summary?.active ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Total Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CalendarClock className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-xl font-bold">{summary?.expiringThisMonth ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Expiring This Month</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="h-5 w-5 text-red-600" />
            <div>
              <p className="text-xl font-bold">{summary?.expired ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Expired</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Ban className="h-5 w-5 text-gray-500" />
            <div>
              <p className="text-xl font-bold">{summary?.revoked ?? '—'}</p>
              <p className="text-xs text-muted-foreground">Revoked</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-2" onClick={loadLicenses}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Search and filters */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by restaurant, owner, phone, or key..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setPage(1) }}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="expiring_soon">Expiring Soon</TabsTrigger>
            <TabsTrigger value="expired">Expired</TabsTrigger>
            <TabsTrigger value="revoked">Revoked</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><SortHeader sortKeyName="restaurantName" label="Restaurant Name" /></TableHead>
                <TableHead><SortHeader sortKeyName="ownerName" label="Owner" /></TableHead>
                <TableHead><SortHeader sortKeyName="ownerPhone" label="Phone" /></TableHead>
                <TableHead><SortHeader sortKeyName="status" label="Status" /></TableHead>
                <TableHead><SortHeader sortKeyName="issuedAt" label="Issued" /></TableHead>
                <TableHead><SortHeader sortKeyName="expiresAt" label="Expires" /></TableHead>
                <TableHead><SortHeader sortKeyName="daysLeft" label="Days Left" /></TableHead>
                <TableHead><SortHeader sortKeyName="lastSyncedAt" label="Last Sync" /></TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : sortedLicenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {search || statusFilter !== 'all' ? 'No licenses found matching your filters' : 'No licenses generated yet'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedLicenses.map((license) => (
                  <TableRow key={license._id}>
                    <TableCell className="font-medium">{license.restaurantName}</TableCell>
                    <TableCell>{license.ownerName}</TableCell>
                    <TableCell>{license.ownerPhone}</TableCell>
                    <TableCell>{statusBadge(license.status)}</TableCell>
                    <TableCell>{new Date(license.issuedAt).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>{new Date(license.expiresAt).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>{daysLeftDisplay(license.daysLeft)}</TableCell>
                    <TableCell>{lastSyncDisplay(license.lastSyncedAt)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" title="View Details" onClick={() => setDetailLicense(license)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" title="Renew" onClick={() => setRenewingLicense(license)}>
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" title="Copy Key" onClick={() => handleCopyKey(license)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" title="WhatsApp" onClick={() => handleWhatsApp(license)}>
                          <MessageCircle className="h-3.5 w-3.5" />
                        </Button>
                        {license.status !== 'REVOKED' && (
                          <Button variant="outline" size="sm" title="Revoke" onClick={() => handleRevoke(license)}>
                            <XCircle className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" title="Delete" onClick={() => setDeletingLicense(license)}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            <Users className="inline h-3.5 w-3.5 mr-1" />
            {total} license{total !== 1 ? 's' : ''} &middot; Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      <GenerateOrdoreMiniLicenseModal
        open={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onSuccess={handleGenerateSuccess}
      />

      <RenewOrdoreMiniLicenseModal
        license={renewingLicense}
        open={!!renewingLicense}
        onClose={() => setRenewingLicense(null)}
        onSuccess={handleRenewSuccess}
      />

      <OrdoreMiniLicenseDetailPanel
        license={detailLicense}
        open={!!detailLicense}
        onClose={() => setDetailLicense(null)}
        onRenew={(license) => { setDetailLicense(null); setRenewingLicense(license) }}
        onRevoke={handleRevoke}
      />

      <DeleteOrdoreMiniLicenseModal
        license={deletingLicense}
        open={!!deletingLicense}
        onClose={() => setDeletingLicense(null)}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  )
}
